import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Select, LoadingSkeleton, Pagination, Modal } from '@nexora/design-system';
import {
  useModuleCtx,
  useRetentionMinimums,
  useCreateRetentionMinimum,
  useUpdateRetentionMinimum,
  useDeleteRetentionMinimum,
  useRetentionExceptions,
  useCreateRetentionException,
  useUpdateRetentionException,
  useDeleteRetentionException,
  useRetentionAuditLog,
  useRetentionRules,
  type RetentionMinimum,
  type RetentionMinimumInput,
  type RetentionException,
  type RetentionExceptionInput,
  RULE_STATUS_LABEL,
  LOG_STATUS_LABEL,
  ORIGIN_LABEL,
  logStatusBadgeVariant,
  ruleStatusBadgeVariant,
} from './useRetention';
import { formatCurrency, formatDate, formatDateTime } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Gatilhos e Exceções de Retenção
   (protótipo `erpone-gatilhos-e-exce-es-de-reten-o`)
   ───────────────────────────────────────────────────────────────────────────
   Contratos consumidos (backend real, DTOs em retention.dto.ts):
   - GET  /retention/minimums   → PaginatedRetentionMinimumResponseDto
     POST/PATCH/DELETE /retention/minimums[/:id]
   - GET  /retention/exceptions → PaginatedRetentionExceptionResponseDto
     POST/PATCH/DELETE /retention/exceptions[/:id]
   - GET  /retention/audit-log  → PaginatedRetentionAuditLogResponseDto
   (Regras de lançamento — toggles Retenção na Fonte / Auto-lançamento — são
   propriedades das regras em /retention/rules; o protótipo as mostra como
   configuração global, mas o backend as modela por regra. A tela lê
   /retention/rules para refletir o status atual e permite alternar pela
   página de Regras.)
   ═══════════════════════════════════════════════════════════════════════════ */

interface ToastState {
  message: string;
  variant: 'success' | 'error';
}

/* ── Modais ── */

function MinimumFormModal({
  isOpen,
  onClose,
  minimum,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  minimum?: RetentionMinimum;
  onSubmit: (data: RetentionMinimumInput) => Promise<void>;
}): JSX.Element {
  const [tipoServico, setTipoServico] = useState('');
  const [valorMinimo, setValorMinimo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (minimum) {
      setTipoServico(minimum.tipoServico);
      setValorMinimo(String(minimum.valorMinimo));
    } else {
      setTipoServico('');
      setValorMinimo('');
    }
    setError(null);
  }, [minimum, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!tipoServico.trim()) { setError('Tipo de serviço é obrigatório.'); return; }
    const valorNum = Number(valorMinimo);
    if (!Number.isFinite(valorNum) || valorNum < 0) { setError('Valor mínimo deve ser um número maior ou igual a zero.'); return; }
    try {
      setSubmitting(true);
      await onSubmit({
        tipoServico: tipoServico.trim(),
        valorMinimo: valorNum,
        vigenteDesde: minimum?.vigenteDesde ? minimum.vigenteDesde.slice(0, 10) : new Date().toISOString().slice(0, 10),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar valor mínimo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={minimum ? 'Editar Valor Mínimo' : 'Novo Valor Mínimo'} size="sm">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Tipo de serviço" value={tipoServico} onChange={(e) => setTipoServico(e.target.value)} required placeholder="Ex.: CONSTRUCAO_CIVIL" />
          <Input label="Valor mínimo (R$)" type="number" value={valorMinimo} onChange={(e) => setValorMinimo(e.target.value)} required min={0} />
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : minimum ? 'Salvar alterações' : 'Criar'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function ExceptionFormModal({
  isOpen,
  onClose,
  exception,
  rules,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  exception?: RetentionException;
  rules: { id: string; label: string }[];
  onSubmit: (data: RetentionExceptionInput) => Promise<void>;
}): JSX.Element {
  const [regraId, setRegraId] = useState('');
  const [prestadorCnpj, setPrestadorCnpj] = useState('');
  const [prestadorRazaoSocial, setPrestadorRazaoSocial] = useState('');
  const [motivo, setMotivo] = useState('');
  const [vigenciaInicio, setVigenciaInicio] = useState('');
  const [vigenciaFim, setVigenciaFim] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (exception) {
      setRegraId(exception.regraId ?? '');
      setPrestadorCnpj(exception.prestadorCnpj);
      setPrestadorRazaoSocial(exception.prestadorRazaoSocial ?? '');
      setMotivo(exception.motivo);
      setVigenciaInicio(exception.vigenciaInicio.slice(0, 10));
      setVigenciaFim(exception.vigenciaFim ? exception.vigenciaFim.slice(0, 10) : '');
    } else {
      setRegraId('');
      setPrestadorCnpj('');
      setPrestadorRazaoSocial('');
      setMotivo('');
      setVigenciaInicio(new Date().toISOString().slice(0, 10));
      setVigenciaFim('');
    }
    setError(null);
  }, [exception, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!prestadorCnpj.trim()) { setError('CNPJ do prestador é obrigatório.'); return; }
    if (!motivo.trim()) { setError('Motivo da exceção é obrigatório.'); return; }
    if (!vigenciaInicio) { setError('Início da vigência é obrigatório.'); return; }
    if (vigenciaFim && vigenciaFim < vigenciaInicio) { setError('Fim da vigência não pode ser anterior ao início.'); return; }
    try {
      setSubmitting(true);
      await onSubmit({
        regraId: regraId || undefined,
        prestadorCnpj: prestadorCnpj.trim(),
        prestadorRazaoSocial: prestadorRazaoSocial.trim() || undefined,
        motivo: motivo.trim(),
        vigenciaInicio,
        vigenciaFim: vigenciaFim || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar exceção.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={exception ? 'Editar Exceção' : 'Nova Exceção'} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="CNPJ do prestador" value={prestadorCnpj} onChange={(e) => setPrestadorCnpj(e.target.value)} required placeholder="Ex.: 12345678000190" />
            <Input label="Razão social (opcional)" value={prestadorRazaoSocial} onChange={(e) => setPrestadorRazaoSocial(e.target.value)} placeholder="Ex.: Tech Soluções LTDA" />
          </div>
          <Input label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} required placeholder="Ex.: Simples Nacional — não incidência" />
          <Select label="Regra específica (opcional)" value={regraId} onChange={(e) => setRegraId(e.target.value)}>
            <option value="">Todas as regras</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </Select>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Início da vigência" type="date" value={vigenciaInicio} onChange={(e) => setVigenciaInicio(e.target.value)} required />
            <Input label="Fim da vigência (opcional)" type="date" value={vigenciaFim} onChange={(e) => setVigenciaFim(e.target.value)} />
          </div>
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : exception ? 'Salvar alterações' : 'Criar exceção'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ── Página ── */

export function RetentionExceptionsPage(): JSX.Element {
  const ctx = useModuleCtx();
  const { data: minimumsData, isLoading: minLoading, error: minError } = useRetentionMinimums(ctx);
  const { data: exceptionsData, isLoading: excLoading, error: excError } = useRetentionExceptions(ctx);
  const { data: auditData, isLoading: auditLoading, error: auditError } = useRetentionAuditLog(ctx, { limit: 30 });
  const { data: rulesData } = useRetentionRules(ctx, { limit: 100 });

  const createMinMutation = useCreateRetentionMinimum(ctx);
  const updateMinMutation = useUpdateRetentionMinimum();
  const deleteMinMutation = useDeleteRetentionMinimum(ctx);
  const createExcMutation = useCreateRetentionException(ctx);
  const updateExcMutation = useUpdateRetentionException();
  const deleteExcMutation = useDeleteRetentionException(ctx);

  const [minModalOpen, setMinModalOpen] = useState(false);
  const [editingMin, setEditingMin] = useState<RetentionMinimum | undefined>(undefined);
  const [excModalOpen, setExcModalOpen] = useState(false);
  const [editingExc, setEditingExc] = useState<RetentionException | undefined>(undefined);
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  /* Minimums */
  const handleCreateMin = useCallback(
    async (input: RetentionMinimumInput) => {
      try {
        await createMinMutation.mutateAsync(input);
        showToast('Valor mínimo criado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar valor mínimo.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMinMutation, showToast],
  );

  const handleUpdateMin = useCallback(
    async (input: RetentionMinimumInput) => {
      if (!editingMin) return;
      try {
        await updateMinMutation.mutateAsync({ id: editingMin.id, ...input });
        showToast('Valor mínimo atualizado com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar valor mínimo.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingMin, updateMinMutation, showToast],
  );

  const handleDeleteMin = useCallback(
    async (id: string) => {
      if (!window.confirm('Excluir este valor mínimo?')) return;
      try {
        await deleteMinMutation.mutateAsync(id);
        showToast('Valor mínimo excluído.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir valor mínimo.';
        showToast(msg, 'error');
      }
    },
    [deleteMinMutation, showToast],
  );

  /* Exceptions */
  const handleCreateExc = useCallback(
    async (input: RetentionExceptionInput) => {
      try {
        await createExcMutation.mutateAsync(input);
        showToast('Exceção criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar exceção.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createExcMutation, showToast],
  );

  const handleUpdateExc = useCallback(
    async (input: RetentionExceptionInput) => {
      if (!editingExc) return;
      try {
        await updateExcMutation.mutateAsync({ id: editingExc.id, ...input });
        showToast('Exceção atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar exceção.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingExc, updateExcMutation, showToast],
  );

  const handleDeleteExc = useCallback(
    async (id: string) => {
      if (!window.confirm('Excluir esta exceção?')) return;
      try {
        await deleteExcMutation.mutateAsync(id);
        showToast('Exceção excluída.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir exceção.';
        showToast(msg, 'error');
      }
    },
    [deleteExcMutation, showToast],
  );

  const minimums = minimumsData?.data ?? [];
  const exceptions = exceptionsData?.data ?? [];
  const auditLogs = auditData?.data ?? [];
  const rules = rulesData?.data ?? [];
  const ruleOptions = rules.map((r) => ({
    id: r.id,
    label: `${r.prioridade} · ${ACTION_SHORT[r.acao]} — ${formatConditionShort(r.condicao)}`,
  }));
  const rulesWithRetencaoFonte = rules.filter((r) => r.retencaoNaFonte).length;
  const rulesWithAuto = rules.filter((r) => r.autoLancamento).length;

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>Gatilhos e Exceções de Retenção</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Valores mínimos, regras de lançamento e exceções por prestador.
          </p>
        </div>
        <Button variant="primary" onClick={() => { setEditingExc(undefined); setExcModalOpen(true); }}>+ Nova Exceção</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Valores mínimos', value: String(minimums.length) },
          { label: 'Exceções ativas', value: String(exceptions.filter((e) => e.status === 'ATIVA').length) },
          { label: 'Regras c/ retenção na fonte', value: String(rulesWithRetencaoFonte) },
          { label: 'Regras c/ auto-lançamento', value: String(rulesWithAuto) },
        ].map((s) => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>{s.label}</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#191c1e' }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start', marginBottom: '1.5rem' }}>
        {/* Valores Mínimos */}
        <Card variant="outlined" padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <Button variant="outline" size="sm" onClick={() => { setEditingMin(undefined); setMinModalOpen(true); }}>+ Novo</Button>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Valores Mínimos para Retenção</h3>
          </CardHeader>
          {minLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '0.75rem', color: '#43474e' }}>
              <LoadingSkeleton />
            </div>
          )}
          {minError && !minLoading && (
            <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#ba1a1a', fontSize: '0.875rem' }}>
              Erro ao carregar valores mínimos.
            </div>
          )}
          {!minLoading && !minError && minimums.length === 0 && (
            <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#74777f', fontSize: '0.875rem' }}>
              Nenhum valor mínimo cadastrado.
            </div>
          )}
          {!minLoading && !minError && minimums.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    {['Tipo de serviço', 'Valor mínimo', 'Vigente desde', 'Ações'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === 'Valor mínimo' ? 'right' : 'left',
                          padding: '0.625rem 1rem',
                          background: '#f2f4f6',
                          color: '#43474e',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e0e3e5',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {minimums.map((m) => (
                    <tr key={m.id} style={{ borderBottom: '1px solid #eceef0' }}>
                      <td style={{ padding: '0.625rem 1rem', fontWeight: 500 }}>{m.tipoServico}</td>
                      <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(m.valorMinimo)}</td>
                      <td style={{ padding: '0.625rem 1rem', color: '#74777f' }}>{formatDate(m.vigenteDesde)}</td>
                      <td style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingMin(m); setMinModalOpen(true); }}>Editar</Button>
                        <Button variant="ghost" size="sm" style={{ color: '#ba1a1a' }} onClick={() => handleDeleteMin(m.id)}>Excluir</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Regras de Lançamento (resumo de /retention/rules) */}
        <Card variant="outlined">
          <CardHeader>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Regras de Lançamento</h3>
          </CardHeader>
          <div style={{ padding: '0 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f6f8fa', border: '1px solid #e0e3e5', borderRadius: '8px', padding: '0.75rem 1rem' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>Retenção na Fonte</div>
                <div style={{ fontSize: '0.75rem', color: '#74777f' }}>Imposto descontado do valor a pagar ao fornecedor.</div>
              </div>
              <Badge variant={rulesWithRetencaoFonte > 0 ? 'success' : 'neutral'} size="sm">
                {rulesWithRetencaoFonte > 0 ? `${rulesWithRetencaoFonte} regra(s)` : 'Sem regras'}
              </Badge>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f6f8fa', border: '1px solid #e0e3e5', borderRadius: '8px', padding: '0.75rem 1rem' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>Auto-lançamento (Órgãos Públicos)</div>
                <div style={{ fontSize: '0.75rem', color: '#74777f' }}>Empresa assume recolher e declarar o imposto.</div>
              </div>
              <Badge variant={rulesWithAuto > 0 ? 'success' : 'neutral'} size="sm">
                {rulesWithAuto > 0 ? `${rulesWithAuto} regra(s)` : 'Sem regras'}
              </Badge>
            </div>
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#74777f' }}>
              Esses comportamentos são propriedade de cada regra em <strong>/retention/rules</strong> — ajuste pela página de Regras de Retenção.
            </p>
          </div>
        </Card>
      </div>

      {/* Repositório de Exceções */}
      <Card variant="outlined" padding="0" className="mb-lg">
        <CardHeader
          className="px-md pt-md"
          action={
            <Button variant="outline" size="sm" onClick={() => { setEditingExc(undefined); setExcModalOpen(true); }}>+ Nova Exceção</Button>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Repositório de Exceções</h3>
        </CardHeader>
        {excLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
          </div>
        )}
        {excError && !excLoading && (
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#ba1a1a', fontSize: '0.875rem' }}>Erro ao carregar exceções.</div>
        )}
        {!excLoading && !excError && exceptions.length === 0 && (
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#74777f', fontSize: '0.875rem' }}>
            Nenhuma exceção cadastrada.
          </div>
        )}
        {!excLoading && !excError && exceptions.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Prestador / CNPJ', 'Motivo', 'Vigência', 'Status', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '0.625rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {exceptions.map((e) => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #eceef0' }}>
                    <td style={{ padding: '0.625rem 1rem', fontWeight: 500 }}>
                      {e.prestadorRazaoSocial ?? '—'}
                      <span style={{ display: 'block', fontSize: '0.75rem', color: '#74777f' }}>{e.prestadorCnpj}</span>
                    </td>
                    <td style={{ padding: '0.625rem 1rem', color: '#43474e' }}>{e.motivo}</td>
                    <td style={{ padding: '0.625rem 1rem', color: '#74777f', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                      {formatDate(e.vigenciaInicio)} → {e.vigenciaFim ? formatDate(e.vigenciaFim) : '—'}
                    </td>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <Badge variant={ruleStatusBadgeVariant(e.status)} size="sm">{RULE_STATUS_LABEL[e.status]}</Badge>
                    </td>
                    <td style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingExc(e); setExcModalOpen(true); }}>Editar</Button>
                      <Button variant="ghost" size="sm" style={{ color: '#ba1a1a' }} onClick={() => handleDeleteExc(e.id)}>Excluir</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Log de Auditoria */}
      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Log de Auditoria</h3>
        </CardHeader>
        {auditLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
          </div>
        )}
        {auditError && !auditLoading && (
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#ba1a1a', fontSize: '0.875rem' }}>Erro ao carregar log de auditoria.</div>
        )}
        {!auditLoading && !auditError && auditLogs.length === 0 && (
          <div style={{ padding: '2rem 1.25rem', textAlign: 'center', color: '#74777f', fontSize: '0.875rem' }}>
            Nenhum evento de auditoria registrado.
          </div>
        )}
        {!auditLoading && !auditError && auditLogs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Data/Hora', 'Prestador', 'Valor bruto', 'Status', 'Origem', 'Detalhe'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Valor bruto' ? 'right' : 'left',
                        padding: '0.625rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((l) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #eceef0' }}>
                    <td style={{ padding: '0.625rem 1rem', color: '#74777f', whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                      {formatDateTime(l.dataHora)}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', fontWeight: 500 }}>
                      {l.prestadorRazaoSocial ?? l.prestadorCnpj}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(l.valorBruto)}</td>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <Badge variant={logStatusBadgeVariant(l.status)} size="sm">{LOG_STATUS_LABEL[l.status]}</Badge>
                    </td>
                    <td style={{ padding: '0.625rem 1rem', color: '#43474e', fontSize: '0.8125rem' }}>{ORIGIN_LABEL[l.origem]}</td>
                    <td style={{ padding: '0.625rem 1rem', color: '#74777f', fontSize: '0.8125rem' }}>{l.motivo ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!auditLoading && !auditError && (auditData?.totalPages ?? 1) > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={auditData?.page ?? 1} totalPages={auditData?.totalPages ?? 1} onChange={() => {}} />
          </div>
        )}
      </Card>

      {/* Modals */}
      <MinimumFormModal
        isOpen={minModalOpen}
        onClose={() => setMinModalOpen(false)}
        minimum={editingMin}
        onSubmit={editingMin ? handleUpdateMin : handleCreateMin}
      />
      <ExceptionFormModal
        isOpen={excModalOpen}
        onClose={() => setExcModalOpen(false)}
        exception={editingExc}
        rules={ruleOptions}
        onSubmit={editingExc ? handleUpdateExc : handleCreateExc}
      />

      {toast && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
          }}
        >
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Fechar" style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}>
            &times;
          </button>
        </div>
      )}

      <style>{`
        .module-page { padding: 1.5rem; }
        .mb-lg { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
}

/* Ações/condições curtas para o seletor de regra da exceção */
const ACTION_SHORT: Record<string, string> = {
  RETER_ISS: 'Reter ISS',
  RETER_IRRF: 'Reter IRRF',
  RETER_ISS_IRRF: 'Reter ISS+IRRF',
  NAO_RETER: 'Não Reter',
};

function formatConditionShort(cond: { campo: string; operador: string; valor: number | string | string[] }): string {
  const op = cond.operador;
  if (Array.isArray(cond.valor)) return `${cond.campo} ${op} [${cond.valor.join(', ')}]`;
  return `${cond.campo} ${op} ${cond.valor}`;
}